import { useEffect, useRef } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Subscribe to the real-time /api/events/stream (SSE) feed.
 * Invokes `onMessage(event)` for every `new_message` event.
 * Automatically reconnects on drop (browser default EventSource behaviour,
 * plus an explicit backoff guard if the server actively closes).
 *
 * The handler receives the parsed event payload:
 *   { type, message_id, sender_id, sender_name, sender_type,
 *     recipient_ids, subject, preview, high_priority, created_at }
 */
export default function useMessageStream(onMessage, { enabled = true } = {}) {
  const handlerRef = useRef(onMessage);
  handlerRef.current = onMessage;

  useEffect(() => {
    if (!enabled || !API_URL) return undefined;
    let es;
    let closed = false;
    let reconnectTimer = null;

    const connect = () => {
      if (closed) return;
      es = new EventSource(`${API_URL}/api/events/stream`, { withCredentials: true });
      const dispatch = (e) => {
        try {
          const data = JSON.parse(e.data);
          handlerRef.current && handlerRef.current(data);
        } catch { /* ignore malformed frames */ }
      };
      es.addEventListener('new_message', dispatch);
      es.addEventListener('shift_update', dispatch);
      es.addEventListener('new_email', dispatch);
      es.addEventListener('calendar_changed', dispatch);
      es.addEventListener('crm_changed', dispatch);
      es.addEventListener('customer_updated', dispatch);
      es.addEventListener('booking_updated', dispatch);
      es.addEventListener('shift_swap_requested', dispatch);
      es.addEventListener('shift_swap_resolved', dispatch);
      es.addEventListener('thread_message', dispatch);
      es.addEventListener('thread_read', dispatch);
      es.addEventListener('thread_typing', dispatch);
      es.addEventListener('message_typing', dispatch);
      es.addEventListener('message_read', dispatch);
      es.onerror = () => {
        // EventSource retries automatically, but if the server closed the
        // connection outright we nudge a reconnect after 3s.
        if (es && es.readyState === EventSource.CLOSED && !closed) {
          clearTimeout(reconnectTimer);
          reconnectTimer = setTimeout(connect, 3000);
        }
      };
    };

    connect();
    return () => {
      closed = true;
      clearTimeout(reconnectTimer);
      if (es) es.close();
    };
  }, [enabled]);
}
